import type React from "react";
import type { ComponentPropsWithoutRef } from "react";
import {
	ActivityIndicator,
	Pressable,
	Text,
	type TextStyle,
	type ViewStyle,
	useColorScheme,
} from "react-native";
import { tokens } from "../../design-system/tokens";

export type ButtonVariant = "primary" | "secondary" | "text" | "destructive";
export type ButtonSize = "small" | "medium" | "large";

export interface ButtonProps
	extends Omit<ComponentPropsWithoutRef<typeof Pressable>, "children"> {
	variant?: ButtonVariant;
	size?: ButtonSize;
	loading?: boolean;
	disabled?: boolean;
	fullWidth?: boolean;
	children: string;
}

export const Button: React.FC<ButtonProps> = ({
	variant = "primary",
	size = "medium",
	loading = false,
	disabled = false,
	fullWidth = false,
	children,
	style,
	accessibilityLabel,
	accessibilityHint,
	...pressableProps
}) => {
	const scheme = useColorScheme();
	const colorScheme = scheme === "dark" ? "dark" : "light";
	const colors = tokens.color.semantic[colorScheme];

	const isDisabled = disabled || loading;

	const containerStyles = [
		styles.base,
		styles[`size_${size}`],
		styles[`variant_${variant}_${colorScheme}`],
		fullWidth && styles.fullWidth,
		isDisabled && styles.disabled,
		style,
	] as ViewStyle[];

	const textStyles = [
		styles.text,
		styles[`text_size_${size}`],
		styles[`text_variant_${variant}_${colorScheme}`],
		isDisabled && styles.textDisabled,
	] as TextStyle[];

	return (
		<Pressable
			style={({ pressed }) => [
				...containerStyles,
				pressed && !isDisabled && styles.pressed,
			]}
			disabled={isDisabled}
			accessibilityRole="button"
			accessibilityLabel={accessibilityLabel ?? children}
			accessibilityHint={accessibilityHint}
			accessibilityState={{ disabled: isDisabled, busy: loading }}
			{...pressableProps}
		>
			{loading ? (
				<ActivityIndicator
					color={
						variant === "primary" || variant === "destructive"
							? "#FFFFFF"
							: colors.text.primary
					}
					size={size === "small" ? "small" : "small"}
				/>
			) : (
				<Text style={textStyles}>{children}</Text>
			)}
		</Pressable>
	);
};

const styles = {
	base: {
		flexDirection: "row",
		alignItems: "center",
		justifyContent: "center",
		borderRadius: tokens.radius.md,
	} satisfies ViewStyle,

	size_small: {
		height: 36,
		paddingHorizontal: tokens.spacing.md,
		minWidth: 80,
	} satisfies ViewStyle,

	size_medium: {
		height: 44,
		paddingHorizontal: tokens.spacing.lg,
		minWidth: 100,
	} satisfies ViewStyle,

	size_large: {
		height: 52,
		paddingHorizontal: tokens.spacing.xl,
		minWidth: 120,
	} satisfies ViewStyle,

	fullWidth: {
		width: "100%",
		minWidth: undefined,
	} satisfies ViewStyle,

	variant_primary_light: {
		backgroundColor: tokens.color.semantic.light.accent.indigo,
	} satisfies ViewStyle,

	variant_primary_dark: {
		backgroundColor: tokens.color.semantic.dark.accent.indigo,
	} satisfies ViewStyle,

	variant_secondary_light: {
		backgroundColor: tokens.color.semantic.light.surface.secondary,
		borderWidth: 1,
		borderColor: tokens.color.semantic.light.border.primary,
	} satisfies ViewStyle,

	variant_secondary_dark: {
		backgroundColor: tokens.color.semantic.dark.surface.secondary,
		borderWidth: 1,
		borderColor: tokens.color.semantic.dark.border.primary,
	} satisfies ViewStyle,

	variant_text_light: {
		backgroundColor: "transparent",
	} satisfies ViewStyle,

	variant_text_dark: {
		backgroundColor: "transparent",
	} satisfies ViewStyle,

	variant_destructive_light: {
		backgroundColor: tokens.color.semantic.light.accent.rose,
	} satisfies ViewStyle,

	variant_destructive_dark: {
		backgroundColor: tokens.color.semantic.dark.accent.rose,
	} satisfies ViewStyle,

	pressed: {
		opacity: 0.8,
	} satisfies ViewStyle,

	disabled: {
		opacity: 0.5,
	} satisfies ViewStyle,

	text: {
		fontFamily: tokens.typography.fontFamily.base,
		fontWeight: "600" as TextStyle["fontWeight"],
		textAlign: "center",
	} satisfies TextStyle,

	text_size_small: {
		fontSize: tokens.typography.fontSize.footnote,
		lineHeight:
			tokens.typography.fontSize.footnote * tokens.typography.lineHeight.normal,
	} satisfies TextStyle,

	text_size_medium: {
		fontSize: tokens.typography.fontSize.body,
		lineHeight:
			tokens.typography.fontSize.body * tokens.typography.lineHeight.normal,
	} satisfies TextStyle,

	text_size_large: {
		fontSize: tokens.typography.fontSize.headline,
		lineHeight:
			tokens.typography.fontSize.headline * tokens.typography.lineHeight.normal,
	} satisfies TextStyle,

	text_variant_primary_light: {
		color: "#FFFFFF",
	} satisfies TextStyle,

	text_variant_primary_dark: {
		color: "#FFFFFF",
	} satisfies TextStyle,

	text_variant_secondary_light: {
		color: tokens.color.semantic.light.text.primary,
	} satisfies TextStyle,

	text_variant_secondary_dark: {
		color: tokens.color.semantic.dark.text.primary,
	} satisfies TextStyle,

	text_variant_text_light: {
		color: tokens.color.semantic.light.accent.indigo,
	} satisfies TextStyle,

	text_variant_text_dark: {
		color: tokens.color.semantic.dark.accent.indigo,
	} satisfies TextStyle,

	text_variant_destructive_light: {
		color: "#FFFFFF",
	} satisfies TextStyle,

	text_variant_destructive_dark: {
		color: "#FFFFFF",
	} satisfies TextStyle,

	textDisabled: {
		opacity: 1,
	} satisfies TextStyle,
};
